import React, { useState, useEffect } from 'react';
import './FlashcardsEnhanced.css';

/**
 * Enhanced Flashcards with:
 * - Subject/Topic filtering
 * - Study modes (Sequential, Random, Favorites)
 * - Progress tracking
 * - Confidence ratings
 * - Favorites system
 * - Image support
 * - MCAT relevance display
 */
const FlashcardsEnhanced = ({ data }) => {
  const [currentCard, setCurrentCard] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState('all');
  const [selectedTopic, setSelectedTopic] = useState('all');
  const [studyMode, setStudyMode] = useState('sequential'); // sequential, random, favorites
  const [favorites, setFavorites] = useState(new Set());
  const [confidence, setConfidence] = useState({}); // card_id -> 1-5
  const [studyStats, setStudyStats] = useState({ studied: new Set(), mastered: new Set() });
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    // Load saved data
    const savedFavorites = JSON.parse(localStorage.getItem('flashcardFavorites') || '[]');
    const savedConfidence = JSON.parse(localStorage.getItem('flashcardConfidence') || '{}');
    const savedStats = JSON.parse(localStorage.getItem('flashcardStats') || '{"studied":[],"mastered":[]}');
    
    setFavorites(new Set(savedFavorites));
    setConfidence(savedConfidence);
    setStudyStats({
      studied: new Set(savedStats.studied),
      mastered: new Set(savedStats.mastered)
    });
  }, []);

  if (!data || !data.subjects) {
    return <div className="no-data">No flashcard data available.</div>;
  }

  // Get filtered flashcards
  const getFlashcards = () => {
    let cards = [];
    
    data.subjects.forEach((subject, subjectIdx) => {
      if (selectedSubject !== 'all' && subject.name !== selectedSubject) return;
      
      subject.topics.forEach((topic, topicIdx) => {
        if (selectedTopic !== 'all' && topic.name !== selectedTopic) return;
        
        topic.flashcards.forEach((card, cardIdx) => {
          cards.push({
            ...card,
            subject: subject.name,
            topic: topic.name,
            id: `${subjectIdx}-${topicIdx}-${cardIdx}`
          });
        });
      });
    });

    // Apply study mode filter
    if (studyMode === 'favorites') {
      cards = cards.filter(card => favorites.has(card.id));
    }

    // Shuffle if random mode
    if (studyMode === 'random') {
      cards = cards.sort(() => Math.random() - 0.5);
    }

    return cards;
  };

  const flashcards = getFlashcards();

  if (flashcards.length === 0) {
    return (
      <div className="no-flashcards">
        <h2>No flashcards match your filters</h2>
        <p>Try selecting different subjects or topics</p>
        <button onClick={() => { setSelectedSubject('all'); setSelectedTopic('all'); }}>
          Reset Filters
        </button>
      </div>
    );
  }

  const currentFlashcard = flashcards[currentCard];

  const handleNextCard = () => {
    setIsFlipped(false);
    setCurrentCard((currentCard + 1) % flashcards.length);
    markAsStudied(currentFlashcard.id);
  };

  const handlePrevCard = () => {
    setIsFlipped(false);
    setCurrentCard((currentCard - 1 + flashcards.length) % flashcards.length);
  };

  const toggleFavorite = () => {
    const newFavorites = new Set(favorites);
    if (newFavorites.has(currentFlashcard.id)) {
      newFavorites.delete(currentFlashcard.id);
    } else {
      newFavorites.add(currentFlashcard.id);
    }
    setFavorites(newFavorites);
    localStorage.setItem('flashcardFavorites', JSON.stringify([...newFavorites]));
  };

  const setCardConfidence = (level) => {
    const newConfidence = { ...confidence, [currentFlashcard.id]: level };
    setConfidence(newConfidence);
    localStorage.setItem('flashcardConfidence', JSON.stringify(newConfidence));
    
    // Mark as mastered if confidence is 5
    if (level === 5) {
      const newMastered = new Set(studyStats.mastered);
      newMastered.add(currentFlashcard.id);
      const newStats = { ...studyStats, mastered: newMastered };
      setStudyStats(newStats);
      localStorage.setItem('flashcardStats', JSON.stringify({
        studied: [...newStats.studied],
        mastered: [...newStats.mastered]
      }));
    }
  };

  const markAsStudied = (cardId) => {
    const newStudied = new Set(studyStats.studied);
    newStudied.add(cardId);
    const newStats = { ...studyStats, studied: newStudied };
    setStudyStats(newStats);
    localStorage.setItem('flashcardStats', JSON.stringify({
      studied: [...newStats.studied],
      mastered: [...newStats.mastered]
    }));
  };

  const getProgress = () => {
    const total = flashcards.length;
    const studied = flashcards.filter(card => studyStats.studied.has(card.id)).length;
    const mastered = flashcards.filter(card => studyStats.mastered.has(card.id)).length;
    return { total, studied, mastered };
  };

  const progress = getProgress();

  // Get unique subjects and topics
  const subjects = ['all', ...new Set(data.subjects.map(s => s.name))];
  const topics = selectedSubject === 'all' 
    ? ['all']
    : ['all', ...data.subjects.find(s => s.name === selectedSubject)?.topics.map(t => t.name) || []];

  return (
    <div className="flashcards-enhanced">
      {/* Header with controls */}
      <div className="flashcards-header">
        <h1>📚 Flashcards</h1>
        <button className="settings-btn" onClick={() => setShowSettings(!showSettings)}>
          ⚙️ {showSettings ? 'Hide' : 'Show'} Settings
        </button>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div className="settings-panel">
          <div className="filter-group">
            <label>Subject:</label>
            <select value={selectedSubject} onChange={(e) => {
              setSelectedSubject(e.target.value);
              setSelectedTopic('all');
              setCurrentCard(0);
            }}>
              {subjects.map(subject => (
                <option key={subject} value={subject}>
                  {subject === 'all' ? 'All Subjects' : subject}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Topic:</label>
            <select value={selectedTopic} onChange={(e) => {
              setSelectedTopic(e.target.value);
              setCurrentCard(0);
            }}>
              {topics.map(topic => (
                <option key={topic} value={topic}>
                  {topic === 'all' ? 'All Topics' : topic}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Study Mode:</label>
            <select value={studyMode} onChange={(e) => {
              setStudyMode(e.target.value);
              setCurrentCard(0);
            }}>
              <option value="sequential">Sequential</option>
              <option value="random">Random</option>
              <option value="favorites">Favorites Only</option>
            </select>
          </div>
        </div>
      )}

      {/* Progress Bar */}
      <div className="progress-section">
        <div className="progress-stats">
          <span>📖 Studied: {progress.studied}/{progress.total}</span>
          <span>⭐ Mastered: {progress.mastered}/{progress.total}</span>
          <span>❤️ Favorites: {favorites.size}</span>
        </div>
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${(progress.studied / progress.total) * 100}%` }} />
          <div className="progress-mastered" style={{ width: `${(progress.mastered / progress.total) * 100}%` }} />
        </div>
      </div>

      {/* Flashcard */}
      <div className="flashcard-wrapper">
        <div className="card-meta">
          <span className="subject-label">{currentFlashcard.subject}</span>
          <span className="topic-label">{currentFlashcard.topic}</span>
          <span className="card-counter">{currentCard + 1} / {flashcards.length}</span>
        </div>

        <div 
          className={`flashcard-3d ${isFlipped ? 'flipped' : ''}`}
          onClick={() => setIsFlipped(!isFlipped)}
        >
          {/* Front */}
          <div className="flashcard-face flashcard-front">
            <div className="card-content">
              {currentFlashcard.image_url && (
                <div className="card-image-container">
                  <img src={currentFlashcard.image_url} alt={currentFlashcard.term} />
                  {currentFlashcard.image_description && (
                    <p className="image-caption">{currentFlashcard.image_description}</p>
                  )}
                </div>
              )}
              <h2 className="card-term">{currentFlashcard.term}</h2>
              <p className="flip-hint">Click to flip</p>
            </div>
          </div>

          {/* Back */}
          <div className="flashcard-face flashcard-back">
            <div className="card-content">
              <p className="card-definition">{currentFlashcard.definition}</p>
              
              {currentFlashcard.mcat_relevance && (
                <div className="mcat-relevance">
                  <strong>🎯 MCAT Relevance:</strong>
                  <p>{currentFlashcard.mcat_relevance}</p>
                </div>
              )}

              {currentFlashcard.explanation_link && (
                <a 
                  href={currentFlashcard.explanation_link} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="learn-more-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  📖 Learn More
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Confidence Rating */}
        {isFlipped && (
          <div className="confidence-rating">
            <p>How well do you know this?</p>
            <div className="confidence-buttons">
              {[1, 2, 3, 4, 5].map(level => (
                <button
                  key={level}
                  onClick={() => setCardConfidence(level)}
                  className={`confidence-btn ${confidence[currentFlashcard.id] === level ? 'active' : ''}`}
                >
                  {level === 1 && '😕'}
                  {level === 2 && '🤔'}
                  {level === 3 && '😐'}
                  {level === 4 && '😊'}
                  {level === 5 && '🤩'}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flashcard-controls">
          <button className="nav-btn prev-btn" onClick={handlePrevCard}>
            ← Previous
          </button>
          
          <button 
            className={`favorite-btn ${favorites.has(currentFlashcard.id) ? 'favorited' : ''}`}
            onClick={toggleFavorite}
          >
            {favorites.has(currentFlashcard.id) ? '❤️' : '🤍'}
          </button>
          
          <button className="nav-btn next-btn" onClick={handleNextCard}>
            Next →
          </button>
        </div>
      </div>

      {/* Keyboard shortcuts hint */}
      <div className="keyboard-hints">
        <span>⌨️ Shortcuts:</span>
        <span>Space = Flip</span>
        <span>← → = Navigate</span>
        <span>F = Favorite</span>
      </div>
    </div>
  );
};

export default FlashcardsEnhanced;
